/**
 * Exact Template Match (byte-for-byte) — see GLOSSARY.md → Trial Authoring.
 *
 * A Test Case is a byte-for-byte template match when EVERY one of its Content
 * Items has Origin `template`, with at least one item. This is the unit the #146
 * subsection bulk-approve action targets: it sets Inclusion Decision = Included
 * on every such case that is also undecided.
 *
 * Deliberately stricter than the rounded **Template Match %** (which rounds, so a
 * case shown as "100 %" can still hide a non-template item): here the share is
 * EXACTLY 100 % verbatim. The threshold (`total > 0`, `template === total`) is
 * the load-bearing, mutable logic, so it lives in `packages/domain` — the only
 * one of the three feature packages with Stryker — where it is mutation-tested
 * (Constitution §I). The caller supplies the Origin counts (e.g. from
 * `summariseOrigins`); this function stays pure and dependency-free.
 */

/**
 * The Origin counts the byte-for-byte rule reads: how many of a Test Case's
 * Content Items are `template`-origin, and how many there are in total. A
 * structural subset of `ui-suvoda`'s `OriginSummary`, kept minimal so the domain
 * predicate never depends on the presentation layer.
 */
export type TemplateOriginCounts = {
  readonly template: number;
  readonly total: number;
};

/** True iff the case has ≥1 Content Item and every one is `template`-origin. */
export const isExactTemplateMatch = (counts: TemplateOriginCounts): boolean =>
  counts.total > 0 && counts.template === counts.total;
